import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/*
 * Переход экрана (DESIGN.md, «Движение»): 380 мс, ease-in-out, сдвиг на 24 px
 * и прозрачность. Экран не прячется заранее: анимация запускается Web
 * Animations API поверх уже показанного содержимого, и если API нет или
 * запуск не удался, человек просто видит новый экран сразу. Меняются только
 * opacity и transform; после конца анимации стили не остаются (fill: none).
 */
const DURATION_MS = 380;
const SHIFT_PX = 24;

interface ScreenTransitionProps {
  className?: string;
  children: ReactNode;
}

export function ScreenTransition({ className, children }: ScreenTransitionProps) {
  const { pathname } = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const shownPath = useRef(pathname);

  // До первой отрисовки, иначе новый экран мигнёт в конечном виде перед стартом.
  useLayoutEffect(() => {
    // Первый показ и повторный вход в тот же адрес (например, смена ?query) без перехода.
    if (shownPath.current === pathname) return;
    shownPath.current = pathname;

    const element = ref.current;
    if (!element || typeof element.animate !== 'function') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    try {
      element.animate(
        [
          { opacity: 0, transform: `translateY(${SHIFT_PX}px)` },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: DURATION_MS, easing: 'ease-in-out' },
      );
    } catch {
      // Анимация — украшение: при сбое экран остаётся видимым как есть.
    }
  }, [pathname]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
